<template>
  <div class="task-detail-modal" v-if="task">
    <div class="task-detail-modal__header">
      <Label v-if="statusLabel" :variant="statusVariant" :show-dot="true">
        {{ statusLabel }}
      </Label>
    </div>

    <p v-if="task.description" class="task-detail-modal__description">
      {{ task.description }}
    </p>

    <div class="task-detail-modal__grid">
      <div class="task-detail-modal__field">
        <span>{{ $t('tasks_view.fields.due_date') }}</span>
        <strong>{{ dueLabel }}</strong>
      </div>
      <div class="task-detail-modal__field">
        <span>{{ $t('tasks_view.fields.priority') }}</span>
        <strong>{{ priorityLabel }}</strong>
      </div>
      <div class="task-detail-modal__field">
        <span>{{ $t('tasks_view.fields.type') }}</span>
        <strong>{{ typeLabel }}</strong>
      </div>
      <div class="task-detail-modal__field">
        <span>{{ $t('tasks_view.fields.assigned_to') }}</span>
        <select
          class="task-detail-modal__select"
          :value="assigneeId"
          :disabled="savingAssignee"
          @change="updateAssignee"
        >
          <option value="">{{ $t('tasks_view.unassigned') }}</option>
          <option v-for="member in memberOptions" :key="member.value" :value="member.value">
            {{ member.label }}
          </option>
        </select>
      </div>
    </div>

    <div v-if="contextLinks.length" class="task-detail-modal__links">
      <span class="task-detail-modal__links-label">{{ $t('tasks_view.home_label') }}</span>
      <button
        v-for="link in contextLinks"
        :key="link.key"
        type="button"
        class="task-detail-modal__link"
        @click="goTo(link)"
      >
        <BaseIcon :name="link.icon" size="14" />
        <span>{{ link.label }}</span>
        <BaseIcon name="chevron" size="12" />
      </button>
    </div>

    <div class="task-detail-modal__actions">
      <Btn :text="$t('common.close')" variant="secondary" @click="close" />
      <Btn
        v-if="isOpen"
        :text="$t('tasks_view.bulk_complete')"
        variant="primary"
        icon="check"
        :loading="savingStatus"
        @click="complete"
      />
      <Btn
        v-else-if="task.status === 'completed'"
        :text="$t('deals.reopen')"
        variant="tertiary"
        :loading="savingStatus"
        @click="reopen"
      />
    </div>
  </div>
  <div v-else class="task-detail-modal__loading">
    <div class="task-detail-modal__spinner"></div>
  </div>
</template>

<script>
  import { ref, computed, onMounted, getCurrentInstance } from 'vue';
  import { useRouter } from 'vue-router';
  import Btn from '@/components/partials/Btn.vue';
  import Label from '@/components/partials/Label.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import { taskService } from '@/services/taskService';
  import OrganizationService from '@/services/organizationService';
  import { useUserStore } from '@/store/userStore';
  import { useToastStore } from '@/store/toastStore';
  import { useUIStore } from '@/store/uiStore';

  export default {
    name: 'TaskDetailModal',
    components: { Btn, Label, BaseIcon },
    props: {
      modalId: { type: [String, Number], required: true },
      taskId: { type: String, required: true },
      /** Optional seed so the modal paints immediately */
      initialTask: { type: Object, default: null },
      onSubmit: { type: Function, default: null },
    },
    setup(props) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;
      const router = useRouter();
      const userStore = useUserStore();
      const toastStore = useToastStore();
      const uiStore = useUIStore();
      const organizationService = new OrganizationService();

      const task = ref(props.initialTask || null);
      const members = ref([]);
      const savingAssignee = ref(false);
      const savingStatus = ref(false);

      const isOpen = computed(
        () => task.value?.status === 'pending' || task.value?.status === 'in_progress'
      );

      const assigneeId = computed(
        () =>
          task.value?.assigned_to ||
          task.value?.assignee?.id ||
          task.value?.assigned_to_user?.id ||
          ''
      );

      const memberOptions = computed(() =>
        members.value
          .map((member) => {
            const user = member.user || member;
            const id = user?.id || member.user_id || member.id;
            const label =
              [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim() ||
              user?.name ||
              user?.email ||
              member.email ||
              id;
            return { value: id, label };
          })
          .filter((m) => m.value)
      );

      const dueLabel = computed(() => {
        if (!task.value?.due_date) return $t('tasks_view.no_due_date');
        const time = task.value.due_time ? ` ${String(task.value.due_time).slice(0, 5)}` : '';
        return `${task.value.due_date}${time}`;
      });

      const priorityLabel = computed(() => {
        const map = {
          high: $t('deals.priority_high'),
          medium: $t('deals.priority_medium'),
          low: $t('deals.priority_low'),
        };
        return map[task.value?.priority] || task.value?.priority || '-';
      });

      const typeLabel = computed(() => {
        const map = {
          general: $t('deals.task_type_general'),
          callback: $t('deals.task_type_callback'),
          follow_up: $t('deals.task_type_follow_up'),
        };
        return map[task.value?.task_type] || task.value?.task_type || $t('deals.task_type_general');
      });

      const statusLabel = computed(() => {
        const map = {
          pending: $t('tasks_view.status_pending'),
          in_progress: $t('tasks_view.status_in_progress'),
          completed: $t('tasks_view.status_completed'),
          cancelled: $t('tasks_view.status_cancelled'),
        };
        return map[task.value?.status] || task.value?.status;
      });

      const statusVariant = computed(() => {
        const map = {
          pending: 'warning',
          in_progress: 'info',
          completed: 'success',
          cancelled: 'default',
        };
        return map[task.value?.status] || 'default';
      });

      const contextLinks = computed(() => {
        if (!task.value) return [];
        const links = [];
        const projectId = task.value.project?.id || task.value.project_id;
        const dealId = task.value.deal?.id || task.value.deal_id;
        const leadId = task.value.lead?.id || task.value.lead_id;

        if (projectId) {
          const number = task.value.project?.project_number;
          const title = task.value.project?.title || '';
          const label = number
            ? `${$t('tasks_view.home_project')} ${number}${title ? ` · ${title}` : ''}`
            : `${$t('tasks_view.home_project')}${title ? ` · ${title}` : ''}`;
          links.push({
            key: 'project',
            icon: 'clipboard',
            label: label.trim() || $t('projects.view_project'),
            to: { name: 'ProjectDetail', params: { id: projectId } },
          });
        }

        if (dealId) {
          const title = task.value.deal?.title;
          links.push({
            key: 'deal',
            icon: 'crm',
            label: title
              ? `${$t('tasks_view.home_deal')} · ${title}`
              : $t('tasks_view.view_deal'),
            to: `/deals/${dealId}`,
          });
        }

        const workOrderId = task.value.work_order?.id || task.value.work_order_id;
        if (workOrderId) {
          const number = task.value.work_order?.work_order_number;
          links.push({
            key: 'work_order',
            icon: 'tool',
            label: number
              ? `${$t('tasks_view.home_work_order')} · ${number}`
              : $t('tasks_view.view_work_order'),
            to: { name: 'WorkOrderDetail', params: { id: workOrderId } },
          });
        }

        if (leadId && !projectId && !dealId) {
          links.push({
            key: 'lead',
            icon: 'user',
            label: task.value.lead?.name || $t('tasks_view.view_lead'),
            to: `/crm/lead/${leadId}`,
          });
        }

        return links;
      });

      const close = () => {
        uiStore.closeModal(props.modalId);
      };

      const notifyUpdated = async () => {
        if (typeof props.onSubmit === 'function') {
          await props.onSubmit(task.value);
        }
      };

      const loadTask = async () => {
        try {
          const response = await taskService.getTaskById(props.taskId);
          task.value = response?.task || response;
        } catch (error) {
          console.error('Failed to load task', error);
          toastStore.addToast({ type: 'error', message: $t('tasks_view.load_failed') });
          close();
        }
      };

      const fetchMembers = async () => {
        const organizationId = userStore.currentOrganization?.id;
        if (!organizationId) return;
        try {
          const response = await organizationService.getMembers(organizationId);
          members.value = Array.isArray(response) ? response : response?.data || [];
        } catch (error) {
          members.value = [];
        }
      };

      const updateAssignee = async (event) => {
        const assignedTo = event.target.value || null;
        const previous = assigneeId.value;
        savingAssignee.value = true;
        try {
          const response = await taskService.updateTask(task.value.id, {
            assigned_to: assignedTo,
          });
          task.value = response?.task || { ...task.value, assigned_to: assignedTo };
          toastStore.addToast({ type: 'success', message: $t('tasks_view.assign_success') });
          await notifyUpdated();
        } catch (error) {
          event.target.value = previous;
          toastStore.addToast({ type: 'error', message: $t('tasks_view.assign_failed') });
        } finally {
          savingAssignee.value = false;
        }
      };

      const complete = async () => {
        savingStatus.value = true;
        try {
          const response = await taskService.completeTask(task.value.id);
          task.value = response?.task || { ...task.value, status: 'completed' };
          toastStore.addToast({ type: 'success', message: $t('deals.task_completed') });
          await notifyUpdated();
        } catch (error) {
          toastStore.addToast({ type: 'error', message: $t('deals.task_update_failed') });
        } finally {
          savingStatus.value = false;
        }
      };

      const reopen = async () => {
        savingStatus.value = true;
        try {
          const response = await taskService.reopenTask(task.value.id);
          task.value = response?.task || { ...task.value, status: 'pending' };
          toastStore.addToast({ type: 'success', message: $t('deals.task_reopened') });
          await notifyUpdated();
        } catch (error) {
          toastStore.addToast({ type: 'error', message: $t('deals.task_update_failed') });
        } finally {
          savingStatus.value = false;
        }
      };

      const goTo = (link) => {
        close();
        if (typeof link.to === 'string') router.push(link.to);
        else router.push(link.to);
      };

      onMounted(async () => {
        await Promise.all([fetchMembers(), loadTask()]);
      });

      return {
        task,
        memberOptions,
        assigneeId,
        savingAssignee,
        savingStatus,
        isOpen,
        dueLabel,
        priorityLabel,
        typeLabel,
        statusLabel,
        statusVariant,
        contextLinks,
        close,
        updateAssignee,
        complete,
        reopen,
        goTo,
      };
    },
  };
</script>

<style scoped lang="scss">
  .task-detail-modal {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    &__header {
      display: flex;
      align-items: center;
      gap: 0.75rem;
    }

    &__description {
      margin: 0;
      @include fontSize('sm');
      color: var(--slate-600);
      white-space: pre-wrap;
    }

    &__grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 0.75rem;

      @include sm-down {
        grid-template-columns: 1fr;
      }
    }

    &__field {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;

      span {
        @include fontSize('sm');
        font-size: 0.75rem;
        color: var(--slate-500);
      }

      strong {
        @include fontSize('sm');
        font-weight: 500;
        color: var(--slate-800);
      }
    }

    &__select {
      width: 100%;
      padding: 0.375rem 0.5rem;
      border: 1px solid var(--slate-200);
      border-radius: 0.375rem;
      background: white;
      @include fontSize('sm');
      color: var(--slate-800);
    }

    &__links {
      display: flex;
      flex-direction: column;
      gap: 0.375rem;
      padding-top: 0.25rem;
      border-top: 1px solid var(--slate-100);
    }

    &__links-label {
      @include fontSize('sm');
      font-size: 0.75rem;
      color: var(--slate-500);
    }

    &__link {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.625rem 0.75rem;
      border: 1px solid var(--slate-200);
      border-radius: 0.5rem;
      background: var(--slate-50);
      color: var(--slate-700);
      @include fontSize('sm');
      font-weight: 500;
      cursor: pointer;
      text-align: left;

      span {
        flex: 1;
      }

      @include hover {
        background: var(--brand-50);
        border-color: var(--brand-100);
        color: var(--brand-700);
      }
    }

    &__actions {
      display: flex;
      justify-content: flex-end;
      gap: 0.5rem;
      margin-top: 0.25rem;
    }

    &__loading {
      display: flex;
      justify-content: center;
      padding: 2rem;
    }

    &__spinner {
      width: 1.25rem;
      height: 1.25rem;
      border: 2px solid var(--slate-200);
      border-top-color: var(--brand-500);
      border-radius: 50%;
      animation: spin 0.6s linear infinite;
    }
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
</style>
